/**
 * Single Pain of Glass Dashboard V2
 * Complete rewrite for < 2 second page load performance
 * Two charts: Funding Trends and M&A Activity with week navigation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { V2DataService, V2Response } from './services/V2DataService';
import { UnifiedChartContainer } from './components/UnifiedChartContainer';
import { V2Navigation } from './components/V2Navigation';
import YearToggleControls, { YearVisibilityState } from './components/YearToggleControls';
import { AdvancedFilterPanel } from './components/AdvancedFilterPanel';
import { useFilterStore, useFilterQueryString } from './stores/FilterStore';
import { log } from './services/LoggingService';
import { MonitoringService } from './monitoring/MonitoringService';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { yearDerivationService } from './services/YearDerivationService';
import { KPICards } from './components/KPICards';
import { calculateKPIMetrics } from './utils/kpiCalculations';
import { categoryDataService, CategoryApiResponse } from './services/CategoryDataService';
import { acquisitionCategoryService, AcquisitionCategoryApiResponse } from './services/AcquisitionCategoryService';
import StageDistributionChart from './components/StageDistributionChart';
import CategoryComparisonChart from './components/CategoryComparisonChart';
import TransactionRangeChart from './components/TransactionRangeChart';
import { isValidV2Response, isValidCategoryApiResponse, isValidAcquisitionCategoryApiResponse, debugV2Response } from './utils/typeGuards';

// Lazy load non-critical components
const GeographicComparisonSelector = React.lazy(() => import('./components/GeographicComparisonSelector').then(module => ({ default: module.GeographicComparisonSelector })));
import { RefreshCw } from 'lucide-react';
// Service worker disabled to fix localhost API calls
// import { serviceWorkerService } from './services/ServiceWorkerService';

// Feature flag for comparison charts
const ENABLE_COMPARISON_CHARTS = import.meta.env.VITE_ENABLE_COMPARISON_CHARTS === 'true' || false;

function V2App() {
  const [data, setData] = useState<V2Response | null>(null);
  const [ttmData, setTtmData] = useState<V2Response | null>(null); // Always fetch TTM for KPI cards
  const [ytdData, setYtdData] = useState<V2Response | null>(null); // Always fetch YTD for KPI cards
  const [categoryData, setCategoryData] = useState<CategoryApiResponse | null>(null); // Product categories YTD
  const [acquisitionCategoryData, setAcquisitionCategoryData] = useState<AcquisitionCategoryApiResponse | null>(null); // Acquisition categories YTD
  const [ttmCategoryData, setTtmCategoryData] = useState<CategoryApiResponse | null>(null); // Product categories TTM
  const [ttmAcquisitionCategoryData, setTtmAcquisitionCategoryData] = useState<AcquisitionCategoryApiResponse | null>(null); // Acquisition categories TTM
  const [selectedTimeframe, setSelectedTimeframe] = useState('1q'); // Default to 1 quarter (13 weeks)
  const [weekOffset, setWeekOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get filters from FilterStore (Zustand)
  // Note: Keep empty arrays as empty arrays, don't convert to undefined
  // Components expect arrays and call .length on them
  const filterStore = useFilterStore();
  const filters = {
    productCategories: filterStore.productCategories,
    categoryDomains: filterStore.categoryDomains,
    stages: filterStore.stages,
    stageBuckets: filterStore.stageBuckets,
    transactionRanges: filterStore.transactionRanges,
    countries: filterStore.countries,
    regions: filterStore.regions,
    continents: filterStore.continents,
    acquisitionTypes: filterStore.acquisitionTypes,
    companyTypes: filterStore.companyTypes,
  };
  const [retryCount, setRetryCount] = useState(0);
  const [dataService] = useState(() => new V2DataService());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Global time grouping state - controls ALL charts (main + comparison)
  const [globalTimeGrouping, setGlobalTimeGrouping] = useState<'weeks' | 'months' | 'quarters'>('weeks');

  // Global year visibility state - controls all charts (now dynamic)
  const [yearVisibility, setYearVisibility] = useState<YearVisibilityState>(() => {
    const yearContext = yearDerivationService.getYearContext();
    return {
      show2023: yearContext.availableYears.includes(2023),
      show2024: yearContext.availableYears.includes(2024),
      show2025: yearContext.availableYears.includes(2025)
    };
  });

  const maxRetries = 3;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      log.debug(`Loading V2 data: ${selectedTimeframe}, offset: ${weekOffset}`, "Debug");
      const startTime = Date.now();

      // Load chart data (which now includes country funding)
      // Also fetch TTM and YTD data in parallel for KPI cards
      // And fetch category data for KPI cards (both YTD and TTM)
      // Use Promise.allSettled to handle partial failures gracefully
      // Apply filters to all data including KPI cards
      log.debug(`Loading V2 data with filters: ${JSON.stringify(filters)}`, "Debug");

      const results = await Promise.allSettled([
        dataService.getChartData(selectedTimeframe, weekOffset, '3year', filters),
        dataService.getChartData('ttm', 0, '3year', filters), // TTM with filters for KPIs
        dataService.getChartData('ytd', 0, '3year', filters), // YTD with filters for KPIs
        categoryDataService.getCategoryData('ytd', 0, filters), // Product categories YTD
        acquisitionCategoryService.getAcquisitionCategoryData('ytd', 0, filters), // Acquisition categories YTD
        categoryDataService.getCategoryData('ttm', 0, filters), // Product categories TTM
        acquisitionCategoryService.getAcquisitionCategoryData('ttm', 0, filters) // Acquisition categories TTM
      ]);

      const loadTime = Date.now() - startTime;
      log.debug(`V2 data loaded in ${loadTime}ms (target: <2000ms)`, "Debug");

      // Extract successful responses and handle failures gracefully
      const [mainResult, ttmResult, ytdResult, prodCatResult, acqCatResult, ttmProdCatResult, ttmAcqCatResult] = results;

      // Set main chart data (required) - VALIDATE BEFORE SETTING
      if (mainResult.status === 'fulfilled') {
        if (isValidV2Response(mainResult.value)) {
          setData(mainResult.value);
        } else {
          const issues = debugV2Response(mainResult.value);
          throw new Error(`Invalid main chart data structure: ${issues.join(', ')}`);
        }
      } else {
        throw mainResult.reason; // Main chart data is required
      }

      // Set optional data (KPI cards can work without these) - VALIDATE BEFORE SETTING
      if (ttmResult.status === 'fulfilled') {
        if (isValidV2Response(ttmResult.value)) {
          setTtmData(ttmResult.value);
        } else {
          log.debug('TTM data validation failed, skipping:', debugV2Response(ttmResult.value), "Debug");
          setTtmData(null); // Explicitly set to null for safety
        }
      } else {
        log.debug('Failed to load TTM data:', ttmResult.reason, "Debug");
        setTtmData(null);
      }

      if (ytdResult.status === 'fulfilled') {
        if (isValidV2Response(ytdResult.value)) {
          setYtdData(ytdResult.value);
        } else {
          log.debug('YTD data validation failed, skipping:', debugV2Response(ytdResult.value), "Debug");
          setYtdData(null);
        }
      } else {
        log.debug('Failed to load YTD data:', ytdResult.reason, "Debug");
        setYtdData(null);
      }

      if (prodCatResult.status === 'fulfilled') {
        if (isValidCategoryApiResponse(prodCatResult.value)) {
          setCategoryData(prodCatResult.value);
        } else {
          log.debug('Product category data validation failed, skipping', "Debug");
          setCategoryData(null);
        }
      } else {
        log.debug('Failed to load product category data:', prodCatResult.reason, "Debug");
        setCategoryData(null);
      }

      if (acqCatResult.status === 'fulfilled') {
        if (isValidAcquisitionCategoryApiResponse(acqCatResult.value)) {
          setAcquisitionCategoryData(acqCatResult.value);
        } else {
          log.debug('Acquisition category data validation failed, skipping', "Debug");
          setAcquisitionCategoryData(null);
        }
      } else {
        log.debug('Failed to load acquisition category data:', acqCatResult.reason, "Debug");
        setAcquisitionCategoryData(null);
      }

      if (ttmProdCatResult.status === 'fulfilled') {
        if (isValidCategoryApiResponse(ttmProdCatResult.value)) {
          setTtmCategoryData(ttmProdCatResult.value);
        } else {
          log.debug('TTM product category data validation failed, skipping', "Debug");
          setTtmCategoryData(null);
        }
      } else {
        log.debug('Failed to load TTM product category data:', ttmProdCatResult.reason, "Debug");
        setTtmCategoryData(null);
      }

      if (ttmAcqCatResult.status === 'fulfilled') {
        if (isValidAcquisitionCategoryApiResponse(ttmAcqCatResult.value)) {
          setTtmAcquisitionCategoryData(ttmAcqCatResult.value);
        } else {
          log.debug('TTM acquisition category data validation failed, skipping', "Debug");
          setTtmAcquisitionCategoryData(null);
        }
      } else {
        log.debug('Failed to load TTM acquisition category data:', ttmAcqCatResult.reason, "Debug");
        setTtmAcquisitionCategoryData(null);
      }

      setRetryCount(0);

    } catch (err) {
      console.error('Error loading V2 data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

      // Check if this is a "cache warming" error (503 with CACHE_EMPTY)
      const isCacheWarming = errorMessage.includes('Cache warming in progress') || errorMessage.includes('CACHE_EMPTY');

      if (isCacheWarming) {
        // For cache warming errors, retry more aggressively with shorter intervals
        if (retryCount < maxRetries * 2) { // Allow more retries for cache warming
          log.debug(`Cache is warming... Retry ${retryCount + 1}`, "Debug");
          setRetryCount(prev => prev + 1);
          setError('Cache is warming up. Automatically retrying...'); // Show friendly message
          setTimeout(() => {
            loadData();
          }, 2000); // Fixed 2 second retry interval for cache warming
        } else {
          setError(`Cache warming is taking longer than expected. Please try refreshing the page in a moment.`);
        }
      } else {
        // For other errors, use standard retry logic
        if (retryCount < maxRetries) {
          log.debug(`Retrying... Attempt ${retryCount + 1} of ${maxRetries}`, "Debug");
          setRetryCount(prev => prev + 1);
          setTimeout(() => {
            loadData();
          }, 1000 * (retryCount + 1));
        } else {
          setError(`Failed to load dashboard data: ${errorMessage}`);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [dataService, selectedTimeframe, weekOffset, filters, retryCount, maxRetries]);

  // Load data when timeframe, week offset, or filters change
  // DEBOUNCED to prevent cascade of API calls when stacking multiple filters
  useEffect(() => {
    // Cancel any in-flight requests
    if (abortController) {
      abortController.abort();
      log.debug('Aborted previous data load request', 'V2App');
    }

    // Create new abort controller for this request
    const newController = new AbortController();
    setAbortController(newController);

    // Debounce filter changes to batch rapid updates
    const debounceTimer = setTimeout(() => {
      log.debug('Filter debounce expired, loading data', 'V2App');
      loadData();
    }, 300); // 300ms debounce - balance between responsiveness and performance

    // Cleanup: cancel timer and abort controller on unmount or dependency change
    return () => {
      clearTimeout(debounceTimer);
      newController.abort();
    };
  }, [
    selectedTimeframe,
    weekOffset,
    filterStore.productCategories,
    filterStore.categoryDomains,
    filterStore.stages,
    filterStore.stageBuckets,
    filterStore.transactionRanges,
    filterStore.countries,
    filterStore.regions,
    filterStore.continents,
    filterStore.acquisitionTypes,
    filterStore.companyTypes
  ]);

  // Initialize service worker and preload data on mount (ONCE)
  useEffect(() => {
    const initializeApp = async () => {
      // Initialize monitoring service
      try {
        const monitoringService = MonitoringService.getInstance();
        await monitoringService.initialize();
        console.log('✅ Monitoring service initialized');
      } catch (error) {
        console.warn('⚠️  Failed to initialize monitoring service:', error);
      }

      // Initialize service worker
      try {
        // Unregister any old service workers from previous deployments
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            console.log('Unregistered zombie service worker');
          }
        }

        // Service worker disabled to fix localhost API calls
        // const swRegistered = await serviceWorkerService.register();
        // Service worker disabled to fix localhost API calls
        /*
        if (false) {
          log.debug('Service worker registered successfully', "Debug");

          // Preload critical data into cache
          await serviceWorkerService.preloadCriticalData();

          // Listen for service worker updates
          serviceWorkerService.onUpdate(setHasServiceWorkerUpdate);

          // Setup offline/online listeners
          serviceWorkerService.addConnectionListeners(
            () => setIsOffline(true),
            () => setIsOffline(false)
          );

          // Set initial offline state
          setIsOffline(serviceWorkerService.isOffline());
        }
        */
      } catch (error) {
        console.warn('Service worker initialization failed:', error);
      }

      // Preload common timeframes
      dataService.preloadTimeframes().catch(err =>
        console.warn('Failed to preload timeframes:', err)
      );
    };

    initializeApp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run ONCE on mount - initialization should not re-run

  const handleTimeframeChange = (timeframe: string) => {
    setSelectedTimeframe(timeframe);
    setWeekOffset(0); // Reset to current period when changing timeframe
    setRetryCount(0);
  };

  const handleWeekNavigate = (direction: 'forward' | 'backward') => {
    if (!data?.metadata.navigation) return;

    const { navigation } = data.metadata;

    if (direction === 'backward' && navigation.can_navigate_back) {
      setWeekOffset(navigation.prev_offset);
    } else if (direction === 'forward' && navigation.can_navigate_forward) {
      setWeekOffset(navigation.next_offset);
    }

    setRetryCount(0);
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    loadData();
  };

  const handleCacheRefresh = async () => {
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      log.debug('Manual cache refresh triggered', 'V2App');

      const apiUrl = import.meta.env.VITE_API_URL || 'https://glass.returnonsecurity.com';
      const response = await fetch(`${apiUrl}/api/v2/cache/warm?forceFull=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Cache refresh failed: ${response.statusText}`);
      }

      const result = await response.json();
      log.debug('Cache refresh completed', 'V2App', result);

      // Reload data after refresh
      await loadData();
    } catch (error) {
      console.error('Cache refresh error:', error);
      setError(`Cache refresh failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRefreshing(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#633FF3] mx-auto"></div>
          <h2 className="mt-6 text-xl font-semibold text-gray-900">
            Loading Single Pain of Glass Dashboard
          </h2>
          <p className="mt-2 text-gray-600">
            Fetching {selectedTimeframe} data{weekOffset > 0 ? ` (${weekOffset} weeks back)` : ''}...
          </p>
          {retryCount > 0 && (
            <p className="mt-2 text-sm text-gray-500">
              Retry attempt {retryCount} of {maxRetries}
            </p>
          )}
          <div className="mt-4 text-xs text-gray-400">
            Target: &lt; 2 second load time via KV edge caching
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="mb-4">
            <svg className="mx-auto h-16 w-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 15.5C3.544 17.167 4.506 18.5 6.044 18.5z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Dashboard Unavailable</h3>
          <p className="text-red-600 mb-6">{error}</p>
          <button
            onClick={handleRetry}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#633FF3] hover:bg-[#5a37db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#633FF3] transition-colors"
          >
            Try Again
          </button>
          <p className="mt-4 text-sm text-gray-500">
            If this persists, the worker cache may be warming. Please try again in a moment.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
        </div>
      </div>
    );
  }

  return (
    <AppErrorBoundary
      onError={(error, errorInfo) => {
        console.error('V2App Error:', error, errorInfo);
        // Report to monitoring service
        if (typeof window !== 'undefined' && (window as any).__REACT_ERROR_BOUNDARY__) {
          (window as any).__REACT_ERROR_BOUNDARY__.onError(error, errorInfo);
        }
      }}
      enableRetry={true}
      maxRetries={3}
      showDetails={import.meta.env.DEV}
    >
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      {/* Offline/Update Notifications - Disabled since service worker is commented out */}

      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50 transition-colors">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <div className="flex-1 hidden sm:block" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
              Single Pain of Glass
            </h1>
            <div className="flex-1 flex justify-end space-x-2">
              <button
                onClick={handleCacheRefresh}
                disabled={isRefreshing || loading}
                className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${
                  isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title="Refresh all data from Airtable (full refresh)"
              >
                <RefreshCw
                  className={`h-5 w-5 text-gray-600 dark:text-gray-300 ${
                    isRefreshing ? 'animate-spin' : ''
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* KPI Cards - Always Visible - WITH DEEP VALIDATION */}
        {ttmData &&
         ytdData &&
         ttmData.charts &&
         ytdData.charts &&
         ttmData.charts.funding_trends &&
         ttmData.charts.ma_activity &&
         ytdData.charts.funding_trends &&
         ytdData.charts.ma_activity && (
          <KPICards {...calculateKPIMetrics(
            ttmData.charts.funding_trends,
            ttmData.charts.ma_activity,
            ytdData.charts.funding_trends,
            ytdData.charts.ma_activity,
            categoryData || undefined,
            acquisitionCategoryData || undefined,
            ttmCategoryData || undefined,
            ttmAcquisitionCategoryData || undefined
          )} />
        )}

        {/* Unified Control Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-8">
          {/* Filters Section */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <AdvancedFilterPanel
              disabled={loading}
              onFiltersApplied={() => {
                log.debug('Filters applied, data will reload automatically', 'V2App');
              }}
            />
          </div>

          {/* Period & Navigation Section */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1">Time Period</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {data.metadata.current_period.start} to {data.metadata.current_period.end}
                </p>
              </div>
              {/* Navigation Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleWeekNavigate('backward')}
                  disabled={!data.metadata.navigation.can_navigate_back || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Earlier
                </button>
                <button
                  onClick={() => handleWeekNavigate('forward')}
                  disabled={!data.metadata.navigation.can_navigate_forward || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Later
                </button>
              </div>
            </div>

            {/* Timeframe Selection */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Timeframe</h4>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['1w', '2w', '4w', '1q', '6m', '9m', '5q', 'ytd', 'ttm'].map((tf) => {
                  const labels: Record<string, string> = {
                    '1w': '1 Week',
                    '2w': '2 Weeks',
                    '4w': '4 Weeks',
                    '1q': '1 Quarter',
                    '6m': '6 Months',
                    '9m': '9 Months',
                    '5q': '5 Quarters',
                    'ytd': 'Year to Date',
                    'ttm': 'Trailing 12M'
                  };
                  return (
                    <button
                      key={tf}
                      onClick={() => handleTimeframeChange(tf)}
                      disabled={loading}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                        selectedTimeframe === tf
                          ? 'bg-purple-600 text-white shadow-sm hover:bg-purple-700'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {labels[tf]}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Chart Display Options */}
          <div className="p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Chart Display Options</h3>

            {/* Time Grouping */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Time Grouping</h4>
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1 bg-gray-100 dark:bg-gray-800 shadow-inner">
                {[
                  { key: 'weeks' as const, label: 'Weeks' },
                  { key: 'months' as const, label: 'Months' },
                  { key: 'quarters' as const, label: 'Quarters' }
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setGlobalTimeGrouping(key)}
                    className={`px-5 py-2 text-sm font-medium rounded-md transition-all ${
                      globalTimeGrouping === key
                        ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-md ring-1 ring-purple-600/10'
                        : 'text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Year Visibility */}
            {selectedTimeframe !== 'ttm' && (
              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Year Visibility</h4>
                  <YearToggleControls
                    visibility={yearVisibility}
                    onVisibilityChange={setYearVisibility}
                    mode={'3-series'}
                    seriesNames={data.charts.funding_trends?.series?.map(s => s.name) || yearDerivationService.getSeriesNames()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Charts - Using new HOC-based V2 implementation */}
        <UnifiedChartContainer
          fundingData={data.charts.funding_trends}
          maData={data.charts.ma_activity}
          timeframe={selectedTimeframe}
          weekOffset={weekOffset}
          geoData={data.charts.country_funding}
          weekLabel={data.charts.country_funding?.week || undefined}
          yearVisibility={yearVisibility}
          onYearVisibilityChange={setYearVisibility}
          filters={filters}
          timeGrouping={globalTimeGrouping}
          onTimeGroupingChange={setGlobalTimeGrouping}
          metadata={{
            current_period: data.metadata.current_period,
            cache_status: data.cache_status,
            response_time_ms: data.response_time_ms
          }}
        />

        {/* Additional Analytics Charts - Integrated inline */}
        <StageDistributionChart
          timeframe={selectedTimeframe}
          weekOffset={weekOffset}
          filters={filters}
        />

        <CategoryComparisonChart
          timeframe={selectedTimeframe}
          weekOffset={weekOffset}
          filters={filters}
          category="AI Security"
          type="category_domain"
        />

        <TransactionRangeChart
          timeframe={selectedTimeframe}
          weekOffset={weekOffset}
          filters={filters}
          groupBy="year"
        />

        {/* Geographic Comparison Selector - Feature flagged */}
        {ENABLE_COMPARISON_CHARTS && (
          <React.Suspense
            fallback={
              <div className="mt-6 space-y-6">
                <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading geographic comparison...</span>
                </div>
              </div>
            }
          >
            <div className="mt-8">
              <GeographicComparisonSelector
                timeframe={selectedTimeframe}
                weekOffset={weekOffset}
                filters={filters}
                timeGrouping={globalTimeGrouping}
              />
            </div>
          </React.Suspense>
        )}

      </main>

      {/* Footer */}
      <footer className="mt-8">
        <div className="text-center">
          <a
            href="https://returnonsecurity.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            returnonsecurity.com
          </a>
        </div>
      </footer>
      </div>
    </AppErrorBoundary>
  );
}

export default V2App;